import {
    Box,
    Button,
    Heading,
    Text,
    useBoolean,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout, Logo } from 'components/atoms'
import { EnvironmentFlag } from 'components/atoms/EnvironmentFlag'
import { InputBuild } from 'components/molecules'
import { useAuthContext } from 'contexts/auth'
import { AuthDomain } from 'domains/AuthDomain'
import { environments } from 'environments/environments'
import { AuthRequestDTO } from 'models'
import { errorsAuth } from 'pages/Auth/utils/errorsAuth'
import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useForm } from 'react-hook-form'
import { Link as LinkRouter, useNavigate } from 'react-router-dom'
import { useCountDown } from 'utils/countdown/useCountdown'
import { formatTime } from 'utils/date'
import * as yup from 'yup'
import { CustomError, DataUser } from '../interface'

interface Props {
    setDataUser: (dataUser: DataUser) => void
}

export const FormLogin: React.FC<Props> = ({ setDataUser }) => {
    const navigate = useNavigate()
    const { currentUser, validateCaptcha } = useAuthContext()
    const { CAPTCHA_SITE_KEY } = environments
    const toast = useToast()
    const toastIdRef = React.useRef<any>(null)
    const recaptchaRef = useRef<ReCAPTCHA>(null)

    const [attempt, setAttempt] = useState(0)
    const [timerStopped, stopTimer] = useState(false)
    const [unixEpoch, setUnixEpoch] = useState(0)
    const [btnSubmit, setBtnSubmit] = useState(false)

    const [captcha, setCaptcha] = useState('')

    const schema = yup
        .object({
            nomeUsuario: yup.string().required(),
            senha: yup
                .string()
                .min(8, 'Senha deve ter pelo menos 8 caracteres')
                .max(30, 'Senha deve ter no máximo 30 caracteres')
                .required()
        })
        .required()

    const {
        handleSubmit,
        register,
        getValues,
        watch,
        formState: { errors, isSubmitting }
    } = useForm<AuthRequestDTO>({
        resolver: yupResolver(schema),
        mode: 'all'
    })

    const { setLoadingAuth, setCurrentUser, loadingAuth } = useAuthContext()
    const isAutomation = window.location.href.includes('hasAutomation')
    const [searchView, setSearchView] = useBoolean()

    useEffect(() => {
        const takeValueSession = Number(sessionStorage.getItem('attempts'))
        if (takeValueSession >= 3) setBtnSubmit(true)
        setAttempt(takeValueSession)
    }, [attempt])

    const updatingTry = () => {
        if (attempt <= 3) {
            setAttempt(curent => curent + 1)
            sessionStorage.setItem('attempts', `${attempt + 1}`)

            toast({
                title: `Senha incorreta ${attempt + 1}/3`,
                description: 'Ao errar a senha 3 vezes, você será bloqueado.',
                // duration: null,
                position: 'bottom-left',
                status: 'error',
                isClosable: false,
                variant: 'solid-error'
            })
        } else {
            setBtnSubmit(true)
        }
    }

    const isNegative = (num: any) => {
        if (Math.sign(num) === -1) {
            return false
        }

        return true
    }

    const updateToTimer = () => {
        if (toastIdRef.current && isNegative(minutes)) {
            toast.update(toastIdRef.current, {
                title: `Usuário bloqueado!`,
                description: `Tente novamente em: ${formatTime(
                    minutes,
                    seconds
                )}`,
                duration: minutes === 0 && seconds === 0 ? 1000 : null,
                position: 'bottom-left',
                status: 'error',
                isClosable: true,
                variant: 'solid-error'
            })
        }
    }

    const addToastToTimer = () => {
        toastIdRef.current = toast({
            title: `Usuário bloqueado!`,
            description: `Tente novamente em: ${formatTime(minutes, seconds)}`,
            duration: null,
            position: 'bottom-left',
            status: 'error',
            isClosable: true,
            variant: 'solid-error'
        })
    }

    const timeWhitoutUser = (time: any) => {
        if (time) {
            setUnixEpoch(time)
            setBtnSubmit(true)
            addToastToTimer()
            sessionStorage.removeItem('attempts')
            setAttempt(0)
        }
    }

    const loginError = (error: CustomError) => {
        recaptchaRef.current?.reset()
        error.response.data
            .filter(
                (elem, index, arr) =>
                    index ===
                    arr.findIndex(checkEle => checkEle.codigo === elem.codigo)
            )
            .map(res => {
                if (res.codigo === '0005') {
                    if (attempt > 3) {
                        const error = errorsAuth[res.codigo]

                        toast({
                            title: error.title,
                            description: error.description,
                            position: 'bottom-left',
                            status: 'error',
                            isClosable: true,
                            variant: 'solid-error'
                        })
                    } else {
                        updatingTry()
                    }
                } else if (res.codigo === '4043' && res.bloqueadoAte) {
                    timeWhitoutUser(res.bloqueadoAte)
                } else {
                    const error = errorsAuth[res.codigo]

                    toast({
                        title: error.title,
                        description: error.description,
                        position: 'bottom-left',
                        status: 'error',
                        isClosable: true,
                        variant: 'solid-error'
                    })
                }
            })
    }

    const login = async ({ nomeUsuario, senha }: AuthRequestDTO) => {
        setLoadingAuth(true)
        try {
            const authDomain = new AuthDomain()
            const { isMfa, data } = await authDomain.signin({
                nomeUsuario,
                senha
            })

            if (!isMfa) {
                setCurrentUser(data as any)
                sessionStorage.removeItem('attempts')
                setAttempt(0)
                return
            }

            setDataUser({
                login: nomeUsuario,
                password: senha,
                session: data.session
            })
        } catch (e: any) {
            loginError(e)
        } finally {
            setLoadingAuth(false)
        }
    }

    const validateLoginCaptcha = async (values: AuthRequestDTO) => {
        if (isAutomation) {
            return login(values)
        }

        !captcha &&
            toast({
                title: 'Erro!',
                description: 'Por favor, confirme que você não é um robô.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true,
                variant: 'solid-error'
            })

        validateCaptcha(captcha)
            .then(() => {
                return login(values)
            })
            .catch((error: CustomError) => {
                error.response.data
                    .filter(
                        (elem, index, arr) =>
                            index ===
                            arr.findIndex(
                                checkEle => checkEle.codigo === elem.codigo
                            )
                    )
                    .map(res => {
                        const error = errorsAuth[res.codigo]
                        toast({
                            title: error.title,
                            description: error.description,
                            position: 'bottom-left',
                            status: 'error',
                            isClosable: true,
                            variant: 'solid-error'
                        })
                    })
            })
    }

    const onSubmit = async (values: AuthRequestDTO) => {
        validateLoginCaptcha(values)
    }

    useEffect(() => {
        currentUser && navigate('/instituicoes')
    }, [currentUser])

    const [, hours, minutes, seconds] = useCountDown(timerStopped, unixEpoch)

    useEffect(() => {
        updateToTimer()
        if (minutes === 0 && seconds === 0) {
            stopTimer(true)
            setBtnSubmit(false)
            setUnixEpoch(0)
        }
    }, [timerStopped, minutes, seconds])

    useEffect(() => {
        const { nomeUsuario, senha } = getValues()

        if (nomeUsuario === '' || senha === '') {
            stopTimer(true)
            setBtnSubmit(false)
            setUnixEpoch(0)
        }
    }, [watch('nomeUsuario'), watch('senha')])

    useEffect(() => {
        const currentURL = window.location.href
        const sessionExpired = currentURL.includes('session-expired')

        if (sessionExpired) {
            toast({
                title: 'Sessão expirada',
                description: 'Faça login novamente',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })

            const url = window.location.origin + window.location.pathname
            window.history.pushState({}, document.title, url)
        }
    }, [])

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            h="100vh"
            w="100%"
        >
            <Box display="grid" w="100%" maxW="sm" gap={8}>
                <Box>
                    <Logo maxW={187} />
                </Box>
                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading size="md" textAlign="center" mb="2">
                        Bem vindo de volta!
                    </Heading>
                    <Text fontSize="lg" textAlign="center">
                        Faça login para continuar
                    </Text>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Layout type="container" full px={6} mt={8}>
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                <Layout type="col" layout={{ base: 12 }}>
                                    <InputBuild
                                        id="nomeUsuario"
                                        label="Usuário ou e-mail"
                                        required
                                        err={errors && errors.nomeUsuario}
                                        {...register('nomeUsuario')}
                                    />
                                </Layout>
                                <Layout type="col" layout={{ base: 12 }}>
                                    <InputBuild
                                        id="senha"
                                        label="Senha"
                                        required
                                        err={errors && errors.senha}
                                        iconName={
                                            searchView ? 'eye-slash' : 'eye'
                                        }
                                        iconClick={setSearchView.toggle}
                                        type={searchView ? 'text' : 'password'}
                                        {...register('senha')}
                                    />
                                </Layout>

                                {!isAutomation ? (
                                    <Layout type="col" layout={{ base: 12 }}>
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            width="100%"
                                        >
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                data-testid="captcha-test"
                                                sitekey={CAPTCHA_SITE_KEY}
                                                onChange={e =>
                                                    e && setCaptcha(e)
                                                }
                                            />
                                        </Box>
                                    </Layout>
                                ) : (
                                    <></>
                                )}

                                <Layout type="col" layout={{ base: 12 }}>
                                    <Button
                                        w="full"
                                        colorScheme="primary"
                                        size="md"
                                        type="submit"
                                        data-testid="submitBtn"
                                        isDisabled={btnSubmit}
                                        isLoading={loadingAuth}
                                    >
                                        Entrar
                                    </Button>
                                </Layout>     
                                <Layout type="col" layout={{ base: 12 }}>
                                    <Button
                                        as={LinkRouter}
                                        to="/certificado-digital"
                                        w="full"
                                        colorScheme="primary"
                                        size="md"
                                        variant="outline"
                                        isDisabled={loadingAuth}
                                    >
                                        Autenticação com Certificado Digital
                                    </Button>
                                </Layout>                                                          
                                <Layout type="col" layout={{ base: 12 }}>
                                    <Button
                                        as={LinkRouter}
                                        size="md"
                                        w="full"
                                        colorScheme="primary"
                                        variant="link"
                                        textDecoration="underline"
                                        to="/esqueci-senha"
                                    >
                                        Esqueci a senha
                                    </Button>                                   
                                </Layout>
                                <Text
                                    display="flex"
                                    color="#888891"
                                    fontSize="1rem"
                                    justifyContent="center"
                                >
                                    Versão: {process.env.REACT_APP_VERSION}
                                </Text>

                                <EnvironmentFlag />
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Box>
    )
}
